import {
  ActiveTriggerTypesRequest,
  GetAttributionRequest,
  GetAttributionResponse,
  GetLocationsRequest,
  TriggerFilterOptionsRequest,
} from '@weave/schema-gen-ts/dist/schemas/messaging/etl/amp/v1/service.pb';
import { useQuery, UseQueryOptions } from 'react-query';
import { SchemaMetlAmp } from './services';

export const queryKeys = {
  base: 'messages',
  activeTriggerTypes: (request: ActiveTriggerTypesRequest) => [queryKeys.base, 'active-trigger-types', request],
  getAppointmentsCompleted: (request: GetAttributionRequest) => [queryKeys.base, 'appointments-completed', request],
  getAppointmentsConfirmed: (request: GetAttributionRequest) => [queryKeys.base, 'appointments-confirmed', request],
  getAppointmentsScheduled: (request: GetAttributionRequest) => [queryKeys.base, 'appointments-scheduled', request],
  getLocations: (request: GetLocationsRequest) => [queryKeys.base, 'locations', request],
  getMessagesReplied: (request: GetAttributionRequest) => [queryKeys.base, 'messages-replied', request],
  getMessagesSent: (request: GetAttributionRequest) => [queryKeys.base, 'messages-sent', request],
  triggerFilterOptions: (locationIds: string[]) => [queryKeys.base, 'triggerFilterOptions', locationIds],
};

const defaultOptions = {
  retry: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
};

type QueryOptions = UseQueryOptions<
  GetAttributionResponse,
  unknown,
  GetAttributionResponse,
  (string | GetAttributionRequest)[]
>;

export const useActiveTriggerTypes = (request: ActiveTriggerTypesRequest) =>
  useQuery({
    queryKey: queryKeys.activeTriggerTypes(request),
    queryFn: () => SchemaMetlAmp.ActiveTriggerTypes(request),
    select: (response) => response.hasAppointmentEnteredAt,
    enabled: !!request.locationIds && request.locationIds.length > 0,
  });

export const useGetAppointmentsCompleted = (request: GetAttributionRequest, options: QueryOptions = {}) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.getAppointmentsCompleted(request),
    queryFn: () => SchemaMetlAmp.GetAppointmentsCompleted(request),
    ...options,
  });

export const useGetAppointmentsConfirmed = (request: GetAttributionRequest, options: QueryOptions = {}) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.getAppointmentsConfirmed(request),
    queryFn: () => SchemaMetlAmp.GetAppointmentsConfirmed(request),
    ...options,
  });

export const useGetAppointmentsScheduled = (request: GetAttributionRequest, options: QueryOptions = {}) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.getAppointmentsScheduled(request),
    queryFn: () => SchemaMetlAmp.GetAppointmentsScheduled(request),
    ...options,
  });

export const useGetLocation = (request: GetLocationsRequest) =>
  useQuery({
    queryKey: queryKeys.getLocations(request),
    queryFn: () => SchemaMetlAmp.GetLocations(request),
    select: (data) => data.locations,
    enabled: !!request.orgId,
  });

export const useGetMessagesReplied = (request: GetAttributionRequest, options: QueryOptions = {}) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.getMessagesReplied(request),
    queryFn: () => SchemaMetlAmp.GetMessagesReplied(request),
    ...options,
  });

export const useGetMessagesSent = (request: GetAttributionRequest, options: QueryOptions = {}) =>
  useQuery({
    ...defaultOptions,
    queryKey: queryKeys.getMessagesSent(request),
    queryFn: () => SchemaMetlAmp.GetMessagesSent(request),
    enabled: !!request.orgId && !!request.locationIds?.length,
    ...options,
  });

export const useTriggerFilterOptions = (request: TriggerFilterOptionsRequest) =>
  useQuery({
    queryKey: queryKeys.triggerFilterOptions(request.locationIds ?? []),
    queryFn: (queryFunctionContext) =>
      SchemaMetlAmp.TriggerFilterOptions(request, { signal: queryFunctionContext.signal }),
    select: (data) => data.options,
    enabled: !!request.locationIds?.length,
  });
